import { datadogRum, RumEvent, RumResourceEvent } from '@datadog/browser-rum';
import { CLIENT_ROUTE_PERMISSION_ERROR } from '@strength/domain';
import { environment } from '../environment';

// Each major browser surfaces a rejected fetch() as a different TypeError message.
export const TRANSIENT_FETCH_MESSAGES = new Set([
  'Failed to fetch', // Chromium
  'Load failed', // Safari
  'NetworkError when attempting to fetch resource', // Firefox
]);

/**
 * Matches the browser's generic fetch() rejection surfaced as a TRPCClientError
 * (dropped connection, tab backgrounded mid-request, etc.) rather than a real
 * application bug. Safe to drop from RUM without masking other tRPC errors.
 */
export const isTransientNetworkFetchError = (event: RumEvent): boolean =>
  event.type === 'error' &&
  event.error.type === 'TRPCClientError' &&
  TRANSIENT_FETCH_MESSAGES.has(event.error.message);

/**
 * Same as isTransientNetworkFetchError but for the Okta auth client, which
 * wraps fetch rejections as AuthApiError instead of TRPCClientError.
 */
export const isTransientAuthNetworkError = (event: RumEvent): boolean =>
  event.type === 'error' &&
  event.error.type === 'AuthApiError' &&
  TRANSIENT_FETCH_MESSAGES.has(event.error.message);

/**
 * AbortError is thrown by the browser when an in-flight fetch is cancelled via
 * AbortController.abort() — React Query does this automatically when a
 * component unmounts (e.g. the user navigates away mid-request). These are
 * expected side effects of navigation, not application bugs.
 */
export const isAbortError = (event: RumEvent): boolean =>
  event.type === 'error' && event.error.type === 'AbortError';

/**
 * "Route not permitted" surfaces when a user's role lacks access to a tRPC
 * route. The server correctly returns a 403 and the client throws this error
 * intentionally — it is expected user-permissions behaviour, not an app bug.
 */
export const isRoutePermissionError = (event: RumEvent): boolean =>
  event.type === 'error' &&
  event.error.type === 'TRPCClientError' &&
  event.error.message === CLIENT_ROUTE_PERMISSION_ERROR;

/**
 * Axios surfaces a rejected request as AxiosError: Network Error when the
 * network is unavailable (dropped connection, offline, CORS). This is the
 * Axios equivalent of the browser's fetch() Network Error rejections already
 * filtered above — a transient connectivity condition, not an app bug.
 */
export const isAxiosNetworkError = (event: RumEvent): boolean =>
  event.type === 'error' &&
  event.error.type === 'AxiosError' &&
  event.error.message === 'Network Error';

/**
 * Fired by the browser when a dynamic import() (Vite lazy-loaded chunk) cannot
 * be fetched — dropped connection, stale deployment, CDN blip. The error is a
 * transient network condition, not an application bug, and is indistinguishable
 * from other connectivity failures already filtered above.
 */
export const isModuleImportError = (event: RumEvent): boolean =>
  event.type === 'error' &&
  event.error.type === 'TypeError' &&
  event.error.message === 'Importing a module script failed.';

/**
 * Third-party analytics and auth libraries (e.g. Customer.io, Okta) often
 * catch transient fetch failures internally and re-surface them via
 * console.error(). React itself does the same for any error caught by a
 * class component's componentDidCatch (e.g. TanStack Router's CatchBoundary),
 * regardless of what the boundary's own error handling does. The RUM SDK
 * forwards console.error calls as handled errors (source: "console"). Since
 * the underlying cause is still a transient network condition — not an
 * application bug — these are safe to drop.
 *
 * When console.error() is called with an Error object rather than a plain
 * string, Datadog's console instrumentation formats the message as
 * "<name>: <message>" (e.g. "TRPCClientError: Failed to fetch") instead of
 * the bare message, so an exact match against TRANSIENT_FETCH_MESSAGES misses
 * it — match on the message ending with one of the known transient messages
 * instead.
 */
export const isConsoleTransientNetworkError = (event: RumEvent): boolean =>
  event.type === 'error' &&
  event.error.source === 'console' &&
  [...TRANSIENT_FETCH_MESSAGES].some(
    (transientMessage) =>
      event.error.message === transientMessage ||
      event.error.message.endsWith(`: ${transientMessage}`),
  );

/**
 * Browsers redact error details from cross-origin scripts not loaded with
 * `crossorigin="anonymous"` (plus matching CORS headers from the remote server),
 * replacing the full error with the opaque string "Script error." with an empty
 * stack and no filename. Third-party scripts injected by Gainsight PX, Module
 * Federation remotes (teamworksdev.com, cdn.smartabase.com), and Customer.io's
 * CDN all load cross-origin without that attribute — we cannot add it to
 * third-party loader snippets or node_modules. The events carry no actionable
 * information, so filtering is the only realistic remedy.
 */
export const isScriptError = (event: RumEvent): boolean =>
  event.type === 'error' && event.error.message === 'Script error.';

/**
 * When Chrome's IndexedDB backing store (LevelDB) cannot read or write its files
 * on disk, it force-closes the connection and surfaces an UnknownError reading
 * "Connection is closing because of: IO error: <path>: <reason>". Observed
 * reasons include FILE_ERROR_NO_SPACE (device out of storage, SC-3884) and
 * "File not found" (a file LevelDB's own manifest references disappeared from
 * disk, SC-4115), and the reason string is Chrome-internal so more variants are
 * expected. The path names the per-origin LevelDB directory that backs every
 * IndexedDB database for the origin, not one of ours, so these events carry no
 * stack frames and cannot be attributed to a specific caller (both loco-sync
 * and Zero store data there). All of them describe the device's filesystem
 * failing underneath the browser, not an application bug, and there is no
 * in-app remedy. Matching the whole class keeps the UnknownError type guard so
 * unrelated app errors are still reported.
 */
export const isIndexedDbBackingStoreError = (event: RumEvent): boolean =>
  event.type === 'error' &&
  event.error.type === 'UnknownError' &&
  event.error.message.includes('Connection is closing because of: IO error');

/**
 * Chrome/Edge surface a bare "Internal error." UnknownError when the browser's
 * IndexedDB storage engine hits an internal failure that isn't tied to a
 * specific IO reason (unlike isIndexedDbBackingStoreError above, which matches
 * "Connection is closing because of: IO error: ..."). Observed alongside a
 * handled console error reading "Error cleaning up IDB: could not get database
 * list" — the catch block around `indexedDB.databases()` in `deleteOldDbs`
 * (src/loco-sync/index.tsx) — confirming the failure is the browser's storage
 * backend rejecting that call, not loco-sync or Zero's own code (SC-4222).
 * No stack frames, no attributable caller — same storage-engine failure class
 * as SC-3884/SC-4115, just without a specific reason string.
 */
export const isIndexedDbInternalError = (event: RumEvent): boolean =>
  event.type === 'error' &&
  event.error.type === 'UnknownError' &&
  event.error.message === 'Internal error.';

/**
 * The browser's IndexedDB engine aborts a transaction that sits open with no
 * activity (observed on a backgrounded tab over a slow connection) and
 * surfaces it as an unhandled "Transaction timed out due to inactivity."
 * TimeoutError with no stack frames, so it cannot be attributed to a specific
 * caller — both loco-sync and Zero keep IndexedDB data for this origin, and
 * the abort is the browser reclaiming a stalled transaction, not an
 * application bug. The exact-message match keeps the TimeoutError type guard
 * so our own timeouts (which surface as TRPCClientError/AxiosError, never a
 * bare stackless TimeoutError) are still reported.
 */
export const isIndexedDbTransactionTimeoutError = (event: RumEvent): boolean =>
  event.type === 'error' &&
  event.error.type === 'TimeoutError' &&
  event.error.message === 'Transaction timed out due to inactivity.';

/**
 * Axle redirect tokens are single-use JWTs that expire shortly after issuance.
 * When a user arrives at /auth/axle with an expired or invalid token the server
 * correctly returns 401 and the router's beforeLoad catches the AxiosError and
 * redirects to login — expected behaviour, not an application bug.
 */
export const isAxleTokenExpiredError = (event: RumEvent): boolean =>
  event.type === 'error' &&
  event.error.type === 'AxiosError' &&
  event.error.message === 'Request failed with status code 401' &&
  typeof event.view?.['url'] === 'string' &&
  event.view['url'].includes('/auth/axle');

/**
 * Thrown with zero stack frames — no file, no function — and "EmptyRanges"
 * does not appear anywhere in our source or dependencies. Only ever observed
 * on Safari, consistent with a WebKit-internal or extension-injected script
 * (e.g. AutoFill/password-manager UI) throwing inside the page rather than
 * anything our code references. Not fixable from this repository.
 */
export const isEmptyRangesReferenceError = (event: RumEvent): boolean =>
  event.type === 'error' &&
  event.error.type === 'ReferenceError' &&
  event.error.message === "Can't find variable: EmptyRanges";

/**
 * The messaging page mounts the "messaging" Module Federation remote via
 * @module-federation/bridge-react, which renders the remote into its own,
 * separate React root rather than as a child of our React tree. A crash
 * while that remote — built and deployed independently by another team —
 * renders (React invariant #130, "Element type is invalid") is thrown
 * outside our host app's error boundary and is neither caused by nor
 * fixable from this repository.
 */
export const isMessagingRemoteRenderError = (event: RumEvent): boolean =>
  event.type === 'error' &&
  event.error.message.includes('Minified React error #130;') &&
  typeof event.view?.['url'] === 'string' &&
  event.view['url'].includes('/messaging');

/**
 * Domains whose browser extensions (Perplexity's Comet browser, Honey's
 * coupon extension) inject stylesheets/fonts into every page a user visits.
 * Our CSP blocks the loads, RUM attributes the resulting violations to our
 * app, and the injecting code is not ours — not fixable from this repository.
 */
const EXTENSION_INJECTED_DOMAINS = ['perplexity.ai', 'honey.io'];

/**
 * Matches a hostname against EXTENSION_INJECTED_DOMAINS. Both the bare domain
 * (honey.io) and its subdomains (cdn.honey.io, frontend-cdn.perplexity.ai) are
 * matched, since the extensions serve assets from both. Comparing the parsed
 * hostname — rather than searching a raw URL or message for the domain as a
 * substring — avoids matching a denylisted domain that merely appears
 * somewhere in a path or query string of a URL we do care about, e.g.
 * "https://strength.teamworksapp.com/r?u=https://cdn.honey.io/".
 */
const isExtensionInjectedHostname = (hostname: string | undefined): boolean =>
  hostname !== undefined &&
  EXTENSION_INJECTED_DOMAINS.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
  );

/**
 * Returns the hostname of a URL, or undefined when the value is not a
 * parseable URL — CSP blockedURIs are not always absolute URLs (e.g. the
 * literals "inline" and "eval" for inline script/style violations).
 */
const parseHostname = (url: string): string | undefined => {
  try {
    return new URL(url).hostname;
  } catch {
    return undefined;
  }
};

/**
 * Matches resource events whose URL points at a known extension-injected
 * domain (see EXTENSION_INJECTED_DOMAINS). These only occur for users who
 * have the extension installed, carry no application signal, and are safe
 * to drop.
 */
export const isExtensionInjectedResourceError = (event: RumEvent): boolean => {
  if (event.type !== 'resource') {
    return false;
  }
  const url = (event as RumResourceEvent).resource?.url;

  return (
    typeof url === 'string' && isExtensionInjectedHostname(parseHostname(url))
  );
};

/**
 * The same extension-injected loads also arrive as CSP violation *error*
 * events, not resource events: the RUM SDK listens for the browser's
 * `securitypolicyviolation` event and forwards each one with source "report",
 * the violated directive as the error type (e.g. "font-src") and a message of
 * the form "csp_violation: '<blocked url>' blocked by '<directive>' directive"
 * (see trackReportError in @datadog/browser-rum-core). The blocked URL is only
 * present in that message, so isExtensionInjectedResourceError never sees
 * these. The `csp` field restricts the match to CSP violations; other
 * report-sourced errors (browser intervention/deprecation reports) keep
 * flowing through even if their message happens to mention one of the domains.
 *
 * The blocked URL is parsed back out of that message and matched on hostname,
 * so a denylisted domain appearing only in the path or query of the blocked
 * URL — a violation on one of our own hosts that happens to reference an
 * extension URL — is still reported.
 */
const CSP_BLOCKED_URL_PATTERN = /csp_violation: '([^']*)' blocked by '/;

export const isExtensionInjectedCspViolationError = (
  event: RumEvent,
): boolean => {
  if (
    event.type !== 'error' ||
    event.error.source !== 'report' ||
    event.error.csp === undefined
  ) {
    return false;
  }
  const blockedUrl = CSP_BLOCKED_URL_PATTERN.exec(event.error.message)?.[1];

  return (
    blockedUrl !== undefined &&
    isExtensionInjectedHostname(parseHostname(blockedUrl))
  );
};

export const initDatadogRum = () => {
  const env = environment.VITE_ENVIRONMENT;
  const isGov = environment.VITE_IS_GOV;
  if (env !== 'local') {
    datadogRum.init({
      applicationId: environment.VITE_DD_APPLICATION_ID,
      clientToken: environment.VITE_DD_CLIENT_TOKEN,
      site: environment.VITE_DD_SITE,
      service: 'strength-web',
      env,
      version: environment.VITE_COMMIT_SHA,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: isGov ? 'mask' : 'allow',
      beforeSend: (event) =>
        !isTransientNetworkFetchError(event) &&
        !isTransientAuthNetworkError(event) &&
        !isAbortError(event) &&
        !isRoutePermissionError(event) &&
        !isAxiosNetworkError(event) &&
        !isModuleImportError(event) &&
        !isConsoleTransientNetworkError(event) &&
        !isScriptError(event) &&
        !isIndexedDbBackingStoreError(event) &&
        !isIndexedDbInternalError(event) &&
        !isIndexedDbTransactionTimeoutError(event) &&
        !isAxleTokenExpiredError(event) &&
        !isEmptyRangesReferenceError(event) &&
        !isMessagingRemoteRenderError(event) &&
        !isExtensionInjectedResourceError(event) &&
        !isExtensionInjectedCspViolationError(event),

      allowedTracingUrls: [
        {
          match: (url: string) =>
            url.startsWith(`${environment.VITE_API_URL}/`),
          // 'datadog' for dd-trace on the API; 'tracecontext' (W3C
          // traceparent/tracestate) additionally survives proxies that strip
          // vendor headers and keeps browser spans linked to backend traces.
          propagatorTypes: ['datadog', 'tracecontext'],
        },
      ],
    });
    datadogRum.setGlobalContextProperty('team', 'strength');
    datadogRum.startSessionReplayRecording();
  } else {
    console.info(`Environment is development, skipping Datadog RUM init.`);
  }
};
